import { useState, useEffect, useCallback } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { StytchB2BUIClient } from "@stytch/vanilla-js/b2b";
import { Message, RevealedDocument } from "../types";
import { useSessionStore } from "../store/sessionStore";
import {
  getSessions,
  createSession,
  getSessionMessages,
  getScenario,
  postMessage,
  updateSession,
  deleteSession,
} from "../utils/apiUtil";
import { exportSessionAsZip, exportSessionAsFile } from "../utils/exportUtils";
import { latestSessionForScenario } from "../utils/sessionLookup";

export function useAppData(stytch: StytchB2BUIClient) {
  const sessions            = useSessionStore((s) => s.sessions);
  const setSessions         = useSessionStore((s) => s.setSessions);
  const messages            = useSessionStore((s) => s.messages);
  const setMessages         = useSessionStore((s) => s.setMessages);
  const addMessage          = useSessionStore((s) => s.addMessage);
  const scenario            = useSessionStore((s) => s.scenario);
  const setScenario         = useSessionStore((s) => s.setScenario);
  const setIsLoadingSessions = useSessionStore((s) => s.setIsLoadingSessions);
  const isLoadingMessages   = useSessionStore((s) => s.isLoadingMessages);
  const setIsLoadingMessages = useSessionStore((s) => s.setIsLoadingMessages);
  const error               = useSessionStore((s) => s.error);
  const setError            = useSessionStore((s) => s.setError);
  const updateSessionInList = useSessionStore((s) => s.updateSessionInList);
  const removeSession       = useSessionStore((s) => s.removeSession);

  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  // The active session is identified by the URL so it survives a page
  // refresh and supports deep links. useAppData is mounted above the nested
  // chat route, so the param has to be read via useMatch, not useParams.
  const chatMatch = useMatch("/:orgSlug/chat/:sessionId");
  const activeSessionId = chatMatch?.params.sessionId ?? null;

  const [activePersonaId, setActivePersonaId] = useState<string>("");
  const [revealedDocuments, setRevealedDocuments] = useState<RevealedDocument[]>([]);
  const [inputValues, setInputValues]         = useState<Record<string, string>>({});
  const [loadingStates, setLoadingStates]     = useState<Record<string, boolean>>({});
  const [sessionsLoaded, setSessionsLoaded]   = useState(false);

  const getToken = useCallback(
    () => stytch.session.getTokens()?.session_token,
    [stytch],
  );

  // Fetch scenario when the active session changes
  useEffect(() => {
    if (!activeSessionId) return;
    const session = sessions.find((s) => s.id === activeSessionId);
    if (!session) return;
    if (scenario?.id === session.scenarioId) return;

    const fetchScenario = async () => {
      try {
        const data = await getScenario(session.scenarioId, getToken());
        setScenario(data);
        if (data.personas?.length > 0) setActivePersonaId(data.personas[0].id);
      } catch (e: any) {
        console.error("Failed to load scenario:", e);
        setError(e.message || "Failed to load scenario");
      }
    };
    fetchScenario();
  }, [activeSessionId, sessions]);

  // Fetch sessions on mount
  useEffect(() => {
    const fetchSessions = async () => {
      setIsLoadingSessions(true);
      try {
        const data = await getSessions(getToken());
        setSessions(data);
        setSessionsLoaded(true);
      } catch (e: any) {
        console.error("Failed to load sessions:", e);
        setError(e.message || "Failed to load sessions");
      } finally {
        setIsLoadingSessions(false);
      }
    };
    fetchSessions();
  }, []);

  const activeSessionExists =
    !!activeSessionId && sessions.some((s) => s.id === activeSessionId);

  // If the URL names a session that doesn't exist (deleted, or from another
  // account), surface a 404 once sessions have loaded rather than silently
  // redirecting. We wait for sessionsLoaded so we don't flash a 404 while the
  // list is still being fetched.
  const sessionNotFound =
    !!activeSessionId && sessionsLoaded && !activeSessionExists;

  // Fetch messages once we've confirmed the session exists. Gating on
  // activeSessionExists matters: fetching for an unknown id 500s, which would
  // trip the app-wide error screen instead of showing the 404. While sessions
  // are still loading the id is treated as not-yet-existing and we simply wait.
  useEffect(() => {
    if (!activeSessionId || !activeSessionExists) {
      setMessages([]);
      setRevealedDocuments([]);
      return;
    }
    const fetchMessages = async () => {
      setIsLoadingMessages(true);
      try {
        const data = await getSessionMessages(activeSessionId, getToken());
        setMessages(data);
        const docs: RevealedDocument[] = [];
        data.forEach((msg) => {
          if (msg.revealedDocId && !docs.find((d) => d.documentId === msg.revealedDocId)) {
            docs.push({
              documentId: msg.revealedDocId,
              revealedAt: msg.timestamp,
              revealedByPersonaId: msg.personaId,
            });
          }
        });
        setRevealedDocuments(docs);
      } catch (e: any) {
        console.error("Failed to load messages:", e);
        setError(e.message || "Failed to load messages");
      } finally {
        setIsLoadingMessages(false);
      }
    };
    fetchMessages();
  }, [activeSessionId, activeSessionExists]);

  // Global error listeners
  useEffect(() => {
    const handleError = (event: ErrorEvent) => console.error("Global Error:", event.error);
    const handleRejection = (event: PromiseRejectionEvent) =>
      console.error("Unhandled Rejection:", event.reason);
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await stytch.session.revoke();
      window.location.reload();
    } catch (e) {
      console.error("Logout failed:", e);
    }
  }, [stytch]);

  const handleNewSession = useCallback(async () => {
    if (!scenario) return;
    try {
      const session = await createSession(scenario.id, getToken());
      setSessions([session, ...sessions]);
      navigate(`/${orgSlug}/chat/${session.id}`);
    } catch (e: any) {
      console.error("Failed to create session:", e);
      setError(e.message || "Failed to create session");
    }
  }, [scenario, sessions, orgSlug]);

  const handleBeginScenario = useCallback(
    async (scenarioId: string) => {
      try {
        const session = await createSession(scenarioId, getToken());
        setSessions([session, ...sessions]);
        navigate(`/${orgSlug}/chat/${session.id}`);
      } catch (e: any) {
        console.error("Failed to create session:", e);
        setError(e.message || "Failed to create session");
      }
    },
    [sessions, orgSlug],
  );

  const handleResumeScenario = useCallback(
    (scenarioId: string) => {
      const latest = latestSessionForScenario(sessions, scenarioId);
      if (latest) navigate(`/${orgSlug}/chat/${latest.id}`);
    },
    [sessions, orgSlug],
  );

  const handleRenameSession = useCallback(
    async (sessionId: string, newName: string | null) => {
      try {
        const updated = await updateSession(sessionId, { name: newName }, getToken());
        updateSessionInList(sessionId, {
          name: updated.name,
          lastModifiedAt: updated.lastModifiedAt,
        });
      } catch (e: any) {
        console.error("Failed to rename session:", e);
        setError(e.message || "Failed to rename session");
      }
    },
    [],
  );

  const handleExportChat = useCallback(
    (asZip: boolean) => {
      if (!activeSessionId || !scenario || messages.length === 0) return;
      if (asZip) {
        exportSessionAsZip(messages, scenario.personas, activeSessionId);
      } else {
        exportSessionAsFile(messages, scenario.personas, activeSessionId);
      }
    },
    [activeSessionId, scenario, messages],
  );

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      try {
        await deleteSession(sessionId, getToken());
        removeSession(sessionId);
        if (sessionId === activeSessionId) {
          navigate(`/${orgSlug}/library`, { replace: true });
        }
      } catch (e: any) {
        console.error("Failed to delete session:", e);
        setError(e.message || "Failed to delete session");
      }
    },
    [activeSessionId, orgSlug],
  );

  // onDocumentRevealed lets the caller react to a newly revealed doc (e.g. switch to Documents overlay)
  const handleSendMessage = useCallback(
    async (text: string, onDocumentRevealed: () => void) => {
      if (!text.trim() || !activePersonaId || !scenario || !activeSessionId) return;
      const personaId = activePersonaId;
      const sessionId = activeSessionId;
      const currentPersona = scenario.personas.find((p) => p.id === personaId);
      if (!currentPersona) return;

      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: "user",
        text,
        timestamp: Date.now(),
        personaId,
      };
      addMessage(userMessage);
      setInputValues((prev) => ({ ...prev, [personaId]: "" }));
      setLoadingStates((prev) => ({ ...prev, [personaId]: true }));

      try {
        const response = await postMessage(sessionId, text, personaId, getToken());
        if (response.revealedDocId) {
          const docId = response.revealedDocId;
          setRevealedDocuments((prev) => {
            if (prev.find((d) => d.documentId === docId)) return prev;
            return [
              ...prev,
              { documentId: docId, revealedAt: Date.now(), revealedByPersonaId: personaId },
            ];
          });
          onDocumentRevealed();
        }
        addMessage({
          id: crypto.randomUUID(),
          role: "model",
          text: response.text,
          timestamp: Date.now(),
          personaId,
          revealedDocId: response.revealedDocId,
        });
      } catch (e) {
        console.error("Failed to send message:", e);
        addMessage({
          id: crypto.randomUUID(),
          role: "model",
          text: "Sorry, something went wrong. Please try again.",
          timestamp: Date.now(),
          personaId,
          isError: true,
        });
      } finally {
        setLoadingStates((prev) => ({ ...prev, [personaId]: false }));
      }
    },
    [activePersonaId, scenario, activeSessionId],
  );

  const setCurrentInput = useCallback(
    (val: string) => setInputValues((prev) => ({ ...prev, [activePersonaId]: val })),
    [activePersonaId],
  );

  const activePersona = scenario?.personas.find((p) => p.id === activePersonaId);
  const currentInput  = inputValues[activePersonaId] || "";
  const isLoading     = loadingStates[activePersonaId] || false;

  return {
    // Store state
    sessions,
    activeSessionId,
    sessionNotFound,
    messages,
    scenario,
    isLoadingMessages,
    error,
    // Local state
    activePersonaId,
    setActivePersonaId,
    revealedDocuments,
    inputValues,
    setInputValues,
    setCurrentInput,
    // Handlers
    handleLogout,
    handleNewSession,
    handleBeginScenario,
    handleResumeScenario,
    handleRenameSession,
    handleExportChat,
    handleDeleteSession,
    handleSendMessage,
    // Derived
    activePersona,
    currentInput,
    isLoading,
  };
}
